(() => {
  const app = window.Digitaliz || {};

  app.events = {
    CART_REQUEST_START: "digitaliz:cart:request-start",
    CART_REQUEST_END: "digitaliz:cart:request-end",
    CART_ADD: "digitaliz:cart:add",
    CART_UPDATE: "digitaliz:cart:update",
    CART_REMOVE: "digitaliz:cart:remove",
    CART_REFRESH: "digitaliz:cart:refresh",
    CART_ERROR: "digitaliz:cart:error",
    VARIANT_CHANGE: "digitaliz:variant:change"
  };

  app.emit = (name, detail = {}) => {
    document.dispatchEvent(new CustomEvent(name, { detail }));
  };

  app.on = (name, handler) => {
    document.addEventListener(name, handler);
  };

  // Page scroll lock while a drawer is open. body { overflow: hidden } on its
  // own is ignored by iOS Safari (the page behind the drawer keeps scrolling and
  // steals the drawer's own scrolling), so the body is also fixed in place at
  // its current scroll offset (body.drawer-open in base.css reads the custom
  // property) and scrolled back to that offset when the last drawer closes.
  let lockedScrollY = 0;

  function lockPageScroll() {
    if (document.body.classList.contains("drawer-open")) return;
    lockedScrollY = window.scrollY;
    document.documentElement.style.setProperty("--drawer-scroll-lock-top", `${-lockedScrollY}px`);
    document.body.classList.add("drawer-open");
  }

  function unlockPageScroll() {
    if (!document.body.classList.contains("drawer-open")) return;
    document.body.classList.remove("drawer-open");
    document.documentElement.style.removeProperty("--drawer-scroll-lock-top");
    window.scrollTo(0, lockedScrollY);
  }

  app.openDrawer = (id) => {
    const drawer = document.getElementById(id);
    const backdrop = document.querySelector("[data-drawer-backdrop]");
    if (drawer) drawer.classList.add("is-open");
    if (backdrop) backdrop.classList.add("is-open");
    if (drawer) lockPageScroll();
  };

  app.closeDrawer = (id) => {
    const drawer = document.getElementById(id);
    if (drawer) drawer.classList.remove("is-open");
    const backdrop = document.querySelector("[data-drawer-backdrop]");
    if (!document.querySelector(".theme-drawer.is-open")) {
      if (backdrop) backdrop.classList.remove("is-open");
      unlockPageScroll();
    }
  };

  function formatWithDelimiters(number, precision = 2, thousands = ",", decimal = ".") {
    if (number == null || Number.isNaN(number)) return "0";
    const num = Number(number);
    if (!Number.isFinite(num)) return "0";
    const fixed = (num / 100.0).toFixed(precision);
    const parts = fixed.split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return parts.join(decimal);
  }

  const MONEY_PLACEHOLDER = /\{\{\s*(\w+)\s*\}\}/;
  const MONEY_PLACEHOLDER_ALL = /\{\{\s*\w+\s*\}\}/g;

  // The shop's money format is an HTML string, so it can carry markup around the
  // amount (Shopify apps commonly wrap it, e.g. "<span class=x>£{{amount}}</span>").
  // Text contexts must drop that markup, otherwise the tags print to the screen.
  function stripMoneyMarkup(format) {
    return format.replace(/<[^>]*>/g, "");
  }

  function moneyFormatFor(format) {
    return format || window.themeMoney?.moneyFormat || "${{amount}}";
  }

  // Returns the formatted amount for a given placeholder name, or null if the
  // input isn't a usable number.
  function moneyAmount(cents, placeholder) {
    const raw = typeof cents === "string" ? cents.replace(".", "") : cents;
    const amount = Number(raw);
    if (!Number.isFinite(amount)) return null;

    switch (placeholder) {
      case "amount_no_decimals":
        return formatWithDelimiters(amount, 0, ",", ".");
      case "amount_with_comma_separator":
        return formatWithDelimiters(amount, 2, ".", ",");
      case "amount_no_decimals_with_comma_separator":
        return formatWithDelimiters(amount, 0, ".", ",");
      case "amount_with_apostrophe_separator":
        return formatWithDelimiters(amount, 2, "'", ".");
      case "amount":
      default:
        return formatWithDelimiters(amount, 2, ",", ".");
    }
  }

  function formatMoney(cents, format) {
    const moneyFormat = stripMoneyMarkup(moneyFormatFor(format));
    const match = moneyFormat.match(MONEY_PLACEHOLDER);
    if (!match) return moneyFormat;

    const value = moneyAmount(cents, match[1]);
    if (value === null) return "0";

    return moneyFormat.replace(MONEY_PLACEHOLDER, value);
  }

  // Same result as formatMoney, but the format's markup is rebuilt as real
  // elements so it renders instead of showing up as literal text. Only the
  // format string is parsed as HTML — it comes from the shop's currency
  // settings, never from a customer or an API response. The amount itself is
  // written into text nodes, so cart data is never parsed as HTML.
  function moneyFragment(cents, format) {
    const moneyFormat = moneyFormatFor(format);
    const fragment = document.createDocumentFragment();
    const match = moneyFormat.match(MONEY_PLACEHOLDER);

    if (!match) {
      fragment.appendChild(document.createTextNode(stripMoneyMarkup(moneyFormat)));
      return fragment;
    }

    const value = moneyAmount(cents, match[1]);
    if (value === null) {
      fragment.appendChild(document.createTextNode("0"));
      return fragment;
    }

    const template = document.createElement("template");
    template.innerHTML = moneyFormat;

    const walker = document.createTreeWalker(template.content, NodeFilter.SHOW_TEXT);
    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      if (MONEY_PLACEHOLDER.test(node.data)) {
        node.data = node.data.replace(MONEY_PLACEHOLDER_ALL, value);
      }
    }

    fragment.appendChild(template.content);
    return fragment;
  }

  app.currency = (cents) => formatMoney(cents, window.themeMoney?.moneyFormat);
  app.currencyWithCode = (cents) => formatMoney(cents, window.themeMoney?.moneyWithCurrencyFormat);
  app.currencyNode = (cents) => moneyFragment(cents, window.themeMoney?.moneyFormat);

  // Replaces an element's contents with a formatted price, keeping whatever
  // markup the shop's money format defines. `before` / `after` add plain text
  // around the price (e.g. a minus sign or a unit-price suffix).
  app.setCurrency = (el, cents, options = {}) => {
    if (!el) return;
    const nodes = [];
    if (options.before) nodes.push(document.createTextNode(options.before));
    nodes.push(app.currencyNode(cents));
    if (options.after) nodes.push(document.createTextNode(options.after));
    el.replaceChildren(...nodes);
  };

  function updateHeaderTop() {
    if (!document.querySelector(".site-header.is-sticky-header")) return;
    // Track the announcement bar's bottom edge (works for both sticky and non-sticky bars).
    // When the bar is fixed, its bottom is always barHeight.
    // When the bar is in flow, its bottom decreases as the user scrolls, reaching 0 when off-screen.
    const bar = document.querySelector(".announcement-bar");
    const barBottom = bar ? Math.max(0, bar.getBoundingClientRect().bottom) : 0;
    document.documentElement.style.setProperty("--header-sticky-top", `${barBottom}px`);
  }

  app.updateStickyOffsets = () => {
    const stickyBar = document.querySelector(".announcement-bar.is-sticky-announcement");
    const header = document.querySelector(".site-header.is-sticky-header");

    const stickyBarHeight = stickyBar ? stickyBar.getBoundingClientRect().height : 0;
    const headerHeight = header ? header.getBoundingClientRect().height : 0;

    // body padding-top: only the fixed announcement bar needs compensation
    document.documentElement.style.setProperty("--sticky-bar-offset", `${stickyBarHeight}px`);
    // #MainContent margin-top: accounts for the fixed header above it
    document.documentElement.style.setProperty("--sticky-header-offset", `${headerHeight}px`);
    // kept for any CSS still referencing this variable
    document.documentElement.style.setProperty("--announcement-sticky-height", `${stickyBarHeight}px`);

    updateHeaderTop();
  };

  document.addEventListener("DOMContentLoaded", () => {
    app.updateStickyOffsets();
    window.requestAnimationFrame(app.updateStickyOffsets);
  });
  window.addEventListener("load", () => {
    app.updateStickyOffsets();
  });
  window.addEventListener("resize", () => {
    app.updateStickyOffsets();
  });
  window.addEventListener("scroll", updateHeaderTop, { passive: true });
  document.addEventListener("shopify:section:load", () => {
    app.updateStickyOffsets();
  });

  window.Digitaliz = app;
})();
